import useAnalyticsStore from '~/stores/analytics';

/**
 * TDD-developed analytics composable that provides a clean API for tracking events
 * Uses the analytics Pinia store for state management and queue handling
 * 
 * Developed using Test-Driven Development approach with comprehensive test coverage
 */
export function useAnalytics() {
  const analyticsStore = useAnalyticsStore();

  /**
   * Track a page view event - will be queued and sent in batches
   * This is the primary method for tracking user navigation
   * 
   * @param eventData - Optional event data to override defaults
   */
  const trackPageView = (eventData: {
    page_path?: string;
    page_title?: string;
    referrer?: string;
  } = {}) => {
    analyticsStore.queueEvent({
      page_path: eventData.page_path,
      page_title: eventData.page_title,
      referrer: eventData.referrer,
    });
  };

  /**
   * Manually trigger sending of queued events
   * Useful for testing or ensuring events are sent before user leaves
   */
  const sendQueuedEvents = () => {
    return analyticsStore.sendBatch();
  };

  /**
   * Get current analytics statistics and state
   * Useful for debugging and monitoring
   */
  const getAnalyticsStats = () => ({
    queueSize: analyticsStore.eventQueue.length,
    totalSent: analyticsStore.totalEventsSent,
    totalBatches: analyticsStore.totalBatchesSent,
    totalErrors: analyticsStore.totalErrors,
    isOnline: analyticsStore.isOnline,
    isSending: analyticsStore.isSendingBatch,
    batchSize: analyticsStore.batchSize,
    lastBatchSent: analyticsStore.lastBatchSent,
  });

  /**
   * Clear the analytics queue (use with caution)
   * This will lose any unsent analytics data
   */
  const clearAnalyticsQueue = () => {
    analyticsStore.clearQueue();
  };

  /**
   * Enable/disable localStorage persistence
   * Useful for privacy-conscious users or testing
   */
  const setPersistence = (enabled: boolean) => {
    analyticsStore.persistenceEnabled = enabled;
    if (!enabled) {
      analyticsStore.clearPersistedQueue();
    }
  };

  return {
    // Core tracking
    trackPageView,
    
    // Queue management
    sendQueuedEvents,
    clearAnalyticsQueue,
    
    // Monitoring
    getAnalyticsStats,
    
    // Configuration
    setPersistence,
    
    // Store access for advanced usage (should be used sparingly)
    store: analyticsStore,
  };
}